/* Blue Wattle Pens: variant picker, quantity stepper, add to cart, quick-add. */
(function () {
  const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';

  /* ---- Cart helpers ---- */
  async function addToCart(id, quantity) {
    const res = await fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: Number(quantity) || 1 }] })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');
    await refreshCount();
    return data;
  }

  async function refreshCount() {
    try {
      const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
      document.querySelectorAll('[data-bwp-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        if (el.classList.contains('bwp-count')) el.hidden = cart.item_count === 0;
      });
      document.querySelectorAll('[data-bwp-cart-label]').forEach((el) => {
        el.setAttribute('aria-label', el.dataset.bwpCartLabel.replace('[count]', cart.item_count));
      });
    } catch (e) { /* count stays as rendered */ }
  }

  let toastTimer;
  function toast(message) {
    let el = document.querySelector('.bwp-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'bwp-toast bwp';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.innerHTML = '';
    const text = document.createElement('span');
    text.textContent = message;
    const link = document.createElement('a');
    link.href = root + 'cart';
    link.className = 'bwp-btn bwp-btn--cta';
    link.textContent = 'View cart';
    el.append(text, link);
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 5000);
  }

  /* ---- Quick-add (+) buttons ---- */
  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-bwp-quickadd]');
    if (!btn) return;
    event.preventDefault();
    if (btn.getAttribute('aria-busy') === 'true') return;
    btn.setAttribute('aria-busy', 'true');
    try {
      await addToCart(btn.dataset.variantId, 1);
      toast((btn.dataset.title || 'Item') + ' added to your cart');
    } catch (e) {
      toast(e.message);
    } finally {
      btn.removeAttribute('aria-busy');
    }
  });

  /* ---- Product page ---- */
  class BwpProduct extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('script[type="application/json"][data-bwp-product]');
      if (!json) return;
      this.product = JSON.parse(json.textContent);
      this.qty = 1;
      this.form = this.querySelector('form');
      this.summary = this.querySelector('[data-bwp-summary]');
      this.dot = this.querySelector('[data-bwp-summary-dot]');
      this.idInput = this.querySelector('input[name="id"]');
      this.qtyOut = this.querySelector('[data-bwp-qty]');
      this.qtyInput = this.querySelector('input[name="quantity"]');
      this.submit = this.querySelector('[data-bwp-submit]');
      this.error = this.querySelector('[data-bwp-error]');
      this.price = document.querySelector('[data-bwp-price="' + this.dataset.section + '"]');

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-bwp-option]')) this.onOptionChange();
      });
      this.querySelectorAll('[data-bwp-step]').forEach((b) =>
        b.addEventListener('click', () => this.setQty(this.qty + Number(b.dataset.bwpStep)))
      );
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));

      document.querySelectorAll('[data-bwp-thumb="' + this.dataset.section + '"]').forEach((t) =>
        t.addEventListener('click', () => this.showMedia(t.dataset.mediaId))
      );

      this.onOptionChange(true);
    }

    selectedValues() {
      return this.product.options.map((_, i) => {
        const checked = this.querySelector('[data-bwp-option][data-index="' + i + '"]:checked');
        return checked ? checked.value : null;
      });
    }

    onOptionChange(initial) {
      const values = this.selectedValues();
      // Products with only the default variant render no option inputs: use the variant in the form
      const hasOptions = !!this.querySelector('[data-bwp-option]');
      const variant = hasOptions
        ? this.product.variants.find((v) => v.options.every((o, i) => o === values[i]))
        : this.product.variants.find((v) => String(v.id) === this.idInput.value);
      this.variant = variant;
      this.markAvailability(values);

      // Current value labels next to option names
      values.forEach((v, i) => {
        const label = this.querySelector('[data-bwp-current="' + i + '"]');
        if (label) label.textContent = v || '';
      });

      // Summary row: colour dot plus "Blue, fine nib"
      const colourIndex = Number(this.dataset.colourIndex);
      const parts = [];
      values.forEach((v, i) => {
        if (!v) return;
        const name = this.product.options[i].toLowerCase();
        if (i === colourIndex) parts.push(v);
        else if (name.indexOf('nib') !== -1) parts.push(v.toLowerCase() + ' nib');
        else parts.push(v);
      });
      if (this.summary) {
        this.summary.textContent = parts.length ? parts.join(', ').replace(/^./, (c) => c.toUpperCase()) : this.product.title;
      }
      if (this.dot && colourIndex >= 0) {
        const checked = this.querySelector('[data-bwp-option][data-index="' + colourIndex + '"]:checked');
        const sw = checked && checked.closest('.bwp-swatch');
        if (sw) this.dot.style.background = sw.querySelector('.bwp-swatch__dot').style.background;
      }

      if (!variant) {
        this.setButton(false, this.submit.dataset.unavailable);
        return;
      }
      this.idInput.value = variant.id;
      this.setButton(variant.available, variant.available ? this.submit.dataset.add : this.submit.dataset.soldout);
      if (this.price) {
        const compare = variant.compare_at_price > variant.price ? ' <s>' + this.money(variant.compare_at_price) + '</s>' : '';
        this.price.innerHTML = this.money(variant.price) + compare;
      }
      if (!initial) {
        if (variant.featured_media) this.showMedia(String(variant.featured_media.id));
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    markAvailability(values) {
      // Grey out values that have no available variant with the other current selections
      this.product.options.forEach((_, i) => {
        this.querySelectorAll('[data-bwp-option][data-index="' + i + '"]').forEach((input) => {
          const ok = this.product.variants.some((v) =>
            v.available && v.options.every((o, j) => (j === i ? o === input.value : !values[j] || o === values[j]))
          );
          const wrap = input.closest('.bwp-swatch, .bwp-radio');
          if (wrap) wrap.classList.toggle('is-unavailable', !ok);
        });
      });
    }

    setButton(enabled, text) {
      this.submit.disabled = !enabled;
      this.submit.textContent = text;
    }

    setQty(n) {
      this.qty = Math.max(1, Math.min(99, n));
      this.qtyOut.textContent = this.qty;
      this.qtyInput.value = this.qty;
      const minus = this.querySelector('[data-bwp-step="-1"]');
      if (minus) minus.disabled = this.qty <= 1;
    }

    showMedia(id) {
      const main = document.querySelector('[data-bwp-main="' + this.dataset.section + '"]');
      const thumb = document.querySelector('[data-bwp-thumb="' + this.dataset.section + '"][data-media-id="' + id + '"]');
      if (!main || !thumb) return;
      const img = main.querySelector('img');
      const t = thumb.querySelector('img');
      if (img && t) {
        img.srcset = thumb.dataset.srcset;
        img.src = thumb.dataset.src;
        img.alt = t.alt;
      }
      document.querySelectorAll('[data-bwp-thumb="' + this.dataset.section + '"]').forEach((el) =>
        el.setAttribute('aria-current', el === thumb ? 'true' : 'false')
      );
      const row = thumb.parentElement;
      row.scrollTo({ left: thumb.offsetLeft - row.offsetLeft - 2, behavior: 'smooth' });
    }

    money(cents) {
      // Matches Liquid's money_without_trailing_zeros: $19.99, $10
      const format = this.dataset.moneyFormat || '${{amount}}';
      let amount = (cents / 100).toFixed(2);
      if (amount.endsWith('.00')) amount = amount.slice(0, -3);
      if (format.indexOf('comma_separator') !== -1) amount = amount.replace('.', ',');
      return format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, amount);
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.variant || this.submit.disabled) return;
      this.error.textContent = '';
      this.submit.setAttribute('aria-busy', 'true');
      const label = this.submit.textContent;
      this.submit.textContent = this.submit.dataset.adding;
      try {
        await addToCart(this.variant.id, this.qty);
        toast(this.product.title + ' added to your cart');
      } catch (e) {
        this.error.textContent = e.message;
      } finally {
        this.submit.removeAttribute('aria-busy');
        this.submit.textContent = label;
      }
    }
  }
  if (!customElements.get('bwp-product')) customElements.define('bwp-product', BwpProduct);

  /* ---- Radio groups: arrow keys move within the group (native for radios) ---- */
  /* ---- Accordion: one item open at a time ---- */
  document.addEventListener('toggle', (event) => {
    const d = event.target;
    if (!(d instanceof HTMLDetailsElement) || !d.open) return;
    const group = d.closest('[data-bwp-accordion]');
    if (!group) return;
    group.querySelectorAll('details[open]').forEach((other) => { if (other !== d) other.open = false; });
  }, true);

  /* ---- Mobile menu: close on Escape ---- */
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.bwp-menu[open]').forEach((m) => { m.open = false; m.querySelector('summary').focus(); });
  });
})();
